import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../../../constants/KILO_THEME'
import type { CalibrationHapticBridge } from '../../../lib/calibration/calibrationHaptics'
import { createWebHapticBridge } from '../../../lib/calibration/calibrationHaptics'
import type { AudioSessionController } from '../../../lib/calibration/audioSessionDucking'
import { registerAudioSessionController, unregisterAudioSessionController } from '../../../lib/calibration/audioSessionDucking'
import { defaultRepAdvanceMode } from '../../../lib/calibration/repAdvanceMode'
import type { CalibrationSetResult, RepAdvanceMode, RepConfirmationSource } from '../../../lib/calibration/tempoConstants'
import { useCalibrationSetPacer } from '../../../lib/calibration/useCalibrationSetPacer'
import TutMetronomeRing from './TutMetronomeRing'

export type CalibrationSetTrackerProps = {
  exerciseName: string
  /** Calibration session number (1–4), not set index within a workout. */
  sessionNumber: number
  targetReps: number
  onSetComplete: (result: CalibrationSetResult) => void
  onClose?: () => void
  hapticBridge?: CalibrationHapticBridge
  audioController?: AudioSessionController
  repAdvanceMode?: RepAdvanceMode
  /** When true, show working-weight input on the prep screen. */
  captureWorkingWeight?: boolean
  suggestedWorkingWeightKg?: number | null
}

/**
 * Floor-readable calibration set view: prep → 3-2-1-GO → tempo-paced reps.
 */
const CalibrationSetTracker = ({
  exerciseName,
  sessionNumber,
  targetReps,
  onSetComplete,
  onClose,
  hapticBridge: hapticBridgeProp,
  audioController,
  repAdvanceMode: repAdvanceModeProp,
  captureWorkingWeight = false,
  suggestedWorkingWeightKg = null,
}: CalibrationSetTrackerProps) => {
  const { t } = useTranslation()
  const [workingWeightInput, setWorkingWeightInput] = useState(() =>
    suggestedWorkingWeightKg != null ? String(suggestedWorkingWeightKg) : '',
  )

  useEffect(() => {
    if (suggestedWorkingWeightKg != null) {
      setWorkingWeightInput(String(suggestedWorkingWeightKg))
    }
  }, [suggestedWorkingWeightKg])

  const hapticBridge = useMemo(
    () => hapticBridgeProp ?? createWebHapticBridge(),
    [hapticBridgeProp],
  )
  const repAdvanceMode = useMemo(
    () => repAdvanceModeProp ?? defaultRepAdvanceMode(),
    [repAdvanceModeProp],
  )
  const isManualInput = repAdvanceMode === 'manual'

  const handleSetComplete = useCallback(
    (result: CalibrationSetResult) => {
      if (!captureWorkingWeight) {
        onSetComplete(result)
        return
      }
      const parsed = Number(workingWeightInput.replace(',', '.'))
      onSetComplete({
        ...result,
        workingWeightKg: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined,
      })
    },
    [captureWorkingWeight, onSetComplete, workingWeightInput],
  )

  const {
    phase,
    countdownTick,
    repCount,
    ringVariant,
    isometricFlash,
    banner,
    coachMessage,
    muscularFailure,
    setActive,
    beginCountdown,
    confirmRep,
    endSet,
  } = useCalibrationSetPacer({
    targetReps,
    hapticBridge,
    autoStart: false,
    repAdvanceMode,
    onSetComplete: handleSetComplete,
  })

  useEffect(() => {
    if (!audioController) return
    registerAudioSessionController(audioController)
    return () => unregisterAudioSessionController()
  }, [audioController])

  const awaitingConfirm = phase === 'await_confirm'
  const inPrep = phase === 'prep'
  const inCountdown = phase === 'countdown'

  const handleConfirmRep = useCallback(
    (source: RepConfirmationSource) => {
      if (muscularFailure || !awaitingConfirm) return
      confirmRep(source)
    },
    [awaitingConfirm, confirmRep, muscularFailure],
  )

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (muscularFailure || !awaitingConfirm) return
      if (
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === ' ' ||
        event.key === 'Enter'
      ) {
        event.preventDefault()
        handleConfirmRep('volume_key')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [awaitingConfirm, handleConfirmRep, muscularFailure])

  const phaseLabel =
    phase === 'eccentric'
      ? t('workout.calibration.tracker.descend')
      : phase === 'concentric'
        ? t('workout.calibration.tracker.driveUp')
        : phase === 'isometric'
          ? t('workout.calibration.tracker.hold')
          : phase === 'await_confirm'
            ? t('workout.calibration.tracker.confirm')
            : t('workout.calibration.tracker.set')

  const prepLines = captureWorkingWeight
    ? [
        t('workout.calibration.tracker.prepCompoundLine1'),
        t('workout.calibration.tracker.prepCompoundLine2'),
        t('workout.calibration.tracker.prepCompoundLine3'),
      ]
    : [
        t('workout.calibration.tracker.prepIsolationLine1'),
        t('workout.calibration.tracker.prepIsolationLine2'),
        t('workout.calibration.tracker.prepIsolationLine3'),
      ]

  if (inCountdown) {
    return (
      <div className="fixed inset-0 z-[70] flex min-h-[100dvh] flex-col items-center justify-center bg-black px-6">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.35em] text-neutral-500">
          {t('workout.calibration.tracker.countdownLabel')}
        </p>
        <p
          className="mt-4 font-black tabular-nums leading-none text-kilo-accent"
          style={{ fontSize: 'clamp(6rem, 28vw, 10rem)' }}
        >
          {countdownTick === 0
            ? t('workout.calibration.tracker.countdownGo')
            : countdownTick}
        </p>
        <p className="mt-6 max-w-xs text-center text-sm text-neutral-500">
          {t('workout.calibration.tracker.countdownHint')}
        </p>
      </div>
    )
  }

  if (inPrep) {
    return (
      <div className="fixed inset-0 z-[70] flex min-h-[100dvh] flex-col bg-black px-4 pb-8 pt-6">
        <div className="mb-4">
          <p className={`${KILO.meta} text-kilo-accent`}>
            {t('workout.calibration.tracker.sessionLabel', { number: sessionNumber })}
          </p>
          <h1 className="mt-1 text-3xl font-black uppercase leading-none tracking-tight text-white sm:text-4xl">
            {exerciseName}
          </h1>
        </div>

        <ul className="mb-6 space-y-3 rounded-2xl border border-neutral-800 bg-neutral-950 px-4 py-4 text-sm text-neutral-400">
          {prepLines.map((line) => (
            <li key={line} className="flex gap-2">
              <span className="text-kilo-accent">·</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        {captureWorkingWeight ? (
          <label className="mb-6 block rounded-2xl border border-neutral-800 bg-neutral-950 px-4 py-3">
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">
              {t('workout.calibration.tracker.workingWeight')}
            </span>
            <div className="mt-2 flex items-baseline gap-2">
              <input
                type="number"
                inputMode="decimal"
                min={1}
                step={0.5}
                value={workingWeightInput}
                onChange={(event) => setWorkingWeightInput(event.target.value)}
                className="w-full bg-transparent text-3xl font-black tabular-nums text-white outline-none"
              />
              <span className="text-lg font-bold text-neutral-500">kg</span>
            </div>
          </label>
        ) : null}

        <button
          type="button"
          className="mt-auto flex h-14 w-full items-center justify-center rounded-2xl bg-kilo-accent text-base font-bold text-black"
          onClick={beginCountdown}
        >
          {t('workout.calibration.tracker.beginSet')}
        </button>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-[70] relative flex min-h-[100dvh] flex-col bg-black px-4 pb-8 pt-6 select-none"
      onClick={() => handleConfirmRep('screen_tap')}
      role="button"
      tabIndex={0}
      aria-label={t('workout.calibration.tracker.ariaLabel')}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <p className={`${KILO.meta} text-kilo-accent`}>
            {t('workout.calibration.tracker.sessionLabel', { number: sessionNumber })}
          </p>
          <h1 className="mt-1 text-3xl font-black uppercase leading-none tracking-tight text-white sm:text-4xl">
            {exerciseName}
          </h1>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              endSet()
              onClose()
            }}
            className="rounded-full border border-neutral-800 bg-neutral-900 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-neutral-400"
          >
            {t('workout.calibration.tracker.end')}
          </button>
        ) : null}
      </div>

      {banner ? (
        <div
          className="mb-4 animate-pulse rounded-2xl border-2 border-amber-400 bg-amber-950/80 px-4 py-3 text-center"
          role="alert"
        >
          <p className="text-2xl font-black uppercase tracking-wide text-amber-300">{banner}</p>
        </div>
      ) : null}

      {coachMessage ? (
        <div className="mb-4 rounded-2xl border border-red-500/50 bg-red-950/60 px-4 py-3 text-center">
          <p className="text-xl font-bold uppercase tracking-wide text-red-200">{coachMessage}</p>
          {muscularFailure ? (
            <p className="mt-1 text-sm font-semibold uppercase tracking-widest text-red-400">
              {t('workout.calibration.tracker.muscularFailure')}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <div className="w-full text-center">
          <p className="font-mono text-sm font-bold uppercase tracking-[0.4em] text-neutral-500">{phaseLabel}</p>
          <p
            className="mt-2 font-black tabular-nums leading-none text-white"
            style={{ fontSize: 'clamp(5rem, 22vw, 9rem)' }}
          >
            {repCount}
          </p>
          <p className="mt-1 font-mono text-lg font-bold uppercase tracking-[0.3em] text-neutral-400">
            {t('workout.calibration.tracker.repsTarget', { target: targetReps })}
          </p>
        </div>

        <TutMetronomeRing phase={phase} variant={ringVariant} isometricFlash={isometricFlash} />

        {isManualInput ? (
          <p className="max-w-sm text-center font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-600">
            {awaitingConfirm
              ? t('workout.calibration.tracker.manualConfirmHint')
              : t('workout.calibration.tracker.manualMoveHint')}
          </p>
        ) : (
          <p className="max-w-xs text-center font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-600">
            {t('workout.calibration.tracker.autoHint')}
          </p>
        )}
      </div>

      {setActive && !muscularFailure ? (
        <div className="mt-4 space-y-2" onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            disabled={!awaitingConfirm}
            onClick={() => handleConfirmRep('screen_tap')}
            className={`w-full rounded-2xl border-2 px-6 py-5 text-center font-black uppercase tracking-[0.2em] transition-colors ${
              awaitingConfirm
                ? 'animate-pulse border-kilo-accent bg-kilo-accent/15 text-kilo-accent'
                : 'border-neutral-800 bg-neutral-950 text-neutral-600'
            }`}
          >
            {t('workout.calibration.tracker.confirmRepButton')}
          </button>
          {awaitingConfirm ? (
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">
              {t('workout.calibration.tracker.keyHints')}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default CalibrationSetTracker

export type { RepConfirmationSource, CalibrationSetResult, RepAdvanceMode }
